package nl.volleyplanner.web.controller.planner.rulesets_config_v2

import kotlinx.browser.document
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.span
import nl.volleyplanner.api.entities.OrganisationSeason
import nl.volleyplanner.api.planner.GroupConstraint
import nl.volleyplanner.api.planner.Ruleset
import nl.volleyplanner.api.planner.TeamPouleConfiguration
import nl.volleyplanner.api.sports.BasicTeam
import nl.volleyplanner.sportconfig.NevoboAgeGroup
import nl.volleyplanner.sportconfig.NevoboCompetitionType
import nl.volleyplanner.sportconfig.NevoboTeamName
import nl.volleyplanner.types.Gender
import nl.volleyplanner.web.CoroutineManager.RENDER_SCOPE
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.StatusMessageComponent
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.lib.Tab
import nl.volleyplanner.web.util.SwitchingStateFlow
import org.w3c.dom.HTMLElement
import org.w3c.dom.get

class TeamPouleConfigDataManager(val selectedSeasonFlow: StateFlow<OrganisationSeason?>) {
    private val endpoint = VolleyPlannerApplication.volleyplannerApiClient.organisation.plannerConfiguration
    private val heavyEndpoint = VolleyPlannerApplication.volleyplannerApiPlannerClient.organisation.plannerConfiguration

    private val _refereeGroupConstraintsData = MutableStateFlow<List<GroupConstraint>>(emptyList())
    private val _scoreKeeperGroupConstraintsData = MutableStateFlow<List<GroupConstraint>>(emptyList())
    val refereeGroupConstraintsFlow: StateFlow<List<GroupConstraint>>
        get() = _refereeGroupConstraintsData
    val scoreKeeperGroupConstraintsFlow: StateFlow<List<GroupConstraint>>
        get() = _scoreKeeperGroupConstraintsData

    private val teamPouleConfigState: MutableMap<BasicTeam, MutableStateFlow<List<TeamPouleConfiguration>>> = mutableMapOf()
    private val rulesetsState: MutableMap<String, MutableStateFlow<Ruleset>> = mutableMapOf()

    private val _teamPouleConfigUpdateState: MutableStateFlow<Map<TeamPouleConfiguration, TeamPouleConfiguration>> = MutableStateFlow(emptyMap())
    val teamPouleConfigUpdateTracker: StateFlow<Map<TeamPouleConfiguration, TeamPouleConfiguration>>
        get() = _teamPouleConfigUpdateState

    private val _availableRulesets: MutableStateFlow<List<Ruleset>> = MutableStateFlow(emptyList())
    val availableRulesets: StateFlow<List<Ruleset>>
        get() = _availableRulesets
    private val _availableTeams: MutableStateFlow<List<BasicTeam>> = MutableStateFlow(emptyList())
    val availableTeams: StateFlow<List<BasicTeam>>
        get() = _availableTeams

    init {
        launchAsync(RENDER_SCOPE) {
            selectedSeasonFlow.collect {
                resetAndRefreshData()
            }
        }
    }

    // FIXME: That we need a validation function on first build already says enough
    //  We need a better design pattern here. But now it is too late... Thank me later!
    fun validateState() {
        _teamPouleConfigUpdateState.value.forEach { (original, new) ->
            require(teamPouleConfigState.contains(original.team)) { "team ${original.team} not found in teamPouleConfigState" }
            require(teamPouleConfigState.getValue(original.team).value.contains(original)) { "team ${original.team} does not contain original" }
        }
    }

    suspend fun updateRulesets(newRulesets: List<Ruleset>) {
        heavyEndpoint.getSeasonalEndpoint(selectedSeasonFlow.value!!)
            .bulkCreateOrUpdateRulesets(newRulesets)

        val rulesetsById = newRulesets.associateBy { it.identifier }
        rulesetsState.clear()
        rulesetsById.mapValues { (_, ruleset) -> MutableStateFlow(ruleset) }.let { rulesetsState.putAll(it) }
        _availableRulesets.value = newRulesets.filter { !it.archived }.sortedBy { it.priority }
    }

    suspend fun resetAndRefreshData() {
        _availableTeams.value = emptyList()
        _teamPouleConfigUpdateState.value = emptyMap()
        teamPouleConfigState.forEach { (_, stateFlow) -> stateFlow.value = emptyList() }
        teamPouleConfigState.clear()

        val rulesets = selectedSeasonFlow.value?.let { endpoint.getSeasonalEndpoint(it).listRulesets() } ?: emptyList()
        val rulesetsById = rulesets.associateBy { it.identifier }
        rulesetsState.clear()
        rulesetsById.mapValues { (_, ruleset) -> MutableStateFlow(ruleset) }.let { rulesetsState.putAll(it) }
        _availableRulesets.value = rulesets.sortedBy { it.priority }

        val teamPouleConfigs = selectedSeasonFlow.value?.let { endpoint.getSeasonalEndpoint(it).listTeamPouleConfigs() } ?: emptyList()
        val teamPouleConfigsByTeam = teamPouleConfigs.groupBy { it.team }
        // Remove teams that no longer exist from the state map
        (teamPouleConfigState.keys - teamPouleConfigsByTeam.keys).let { it.forEach { teamPouleConfigState.remove(it) } }
        teamPouleConfigsByTeam.forEach { (team, teamPouleConfigs) ->
            teamPouleConfigState[team] = MutableStateFlow(teamPouleConfigs)
        }
        _availableTeams.value = teamPouleConfigState.keys.toList()

        val refereeConstraints = (selectedSeasonFlow.value?.let { endpoint.getSeasonalEndpoint(it).listSupportedRefereeGroupConstraints() } ?: emptyList())
        _refereeGroupConstraintsData.value = refereeConstraints
        val scoreKeeperConstraints = (selectedSeasonFlow.value?.let { endpoint.getSeasonalEndpoint(it).listSupportedScoreKeeperGroupConstraints() } ?: emptyList())
        _scoreKeeperGroupConstraintsData.value = scoreKeeperConstraints
    }

    fun postTeamPouleConfigUpdate(original: TeamPouleConfiguration, new: TeamPouleConfiguration) {
        _teamPouleConfigUpdateState.value = _teamPouleConfigUpdateState.value + (original to new)
        validateState()
    }

    fun subscribeToTeam(basicTeam: BasicTeam): StateFlow<List<TeamPouleConfiguration>> {
        return teamPouleConfigState.getValue(basicTeam)
    }

    fun subscribeToRuleset(rulesetId: String): StateFlow<Ruleset> {
        return rulesetsState.getValue(rulesetId)
    }
}

class TeamPouleConfigConfiguratorComponent(
    private val dataManager: TeamPouleConfigDataManager,
    private val rulesetSelectorOffcanvas: RulesetSelectorOffcanvas,
    val teamPouleConfiguration: TeamPouleConfiguration
) : HTMLComponent() {
    private val selectedRulesetIdFlow = MutableStateFlow<String?>(teamPouleConfiguration.rulesetIdentifier)
    private val rulesetFlow: SwitchingStateFlow<Ruleset?> = SwitchingStateFlow(
        RENDER_SCOPE,
        selectedRulesetIdFlow.value?.let { dataManager.subscribeToRuleset(it) } ?: MutableStateFlow<Ruleset?>(null)
    )

    init {
        launchAsync(RENDER_SCOPE) {
            selectedRulesetIdFlow.collect { rulesetId -> rulesetFlow.updateSource(
                selectedRulesetIdFlow.value?.let { dataManager.subscribeToRuleset(it) } ?: MutableStateFlow<Ruleset?>(null)
            ) }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val hasChanges = teamPouleConfiguration.rulesetIdentifier != rulesetFlow.value?.identifier
        return consumer.div (classes = "row g-0 border-bottom p-2") {
            if (hasChanges) {
                style = "border-left: 4px solid var(--bs-warning);"
            }
            div (classes = "d-flex flex-wrap align-items-center mb-2") {
                component(RulesetSelectorButtonComponent(
                    dataManager, rulesetSelectorOffcanvas,
                    rulesetFlow.value?.identifier,
                    { ruleset ->
                        selectedRulesetIdFlow.value = ruleset?.identifier
                        dataManager.postTeamPouleConfigUpdate(teamPouleConfiguration, teamPouleConfiguration.copy(rulesetIdentifier = ruleset?.identifier))
                    }
                ))
                h6(classes = "ms-2 mb-0 fs-6 fw-semibold") {
                    +teamPouleConfiguration.poule.name
                    if (hasChanges) {
                        small(classes = "text-secondary ms-2") { +"(gewijzigd)" }
                    }
                }
            }
            if (selectedRulesetIdFlow.value != null && rulesetFlow.value == null) { spinner() } else {
                rulesetCardInfo(rulesetFlow.value)
            }
        }
    }

    override suspend fun getState() = rulesetFlow
}

class SingleTeamRuleConfiguratorComponent(
    private val dataManager: TeamPouleConfigDataManager,
    private val rulesetSelectorOffcanvas: RulesetSelectorOffcanvas,
    val team: BasicTeam,
) : HTMLComponent() {
    val teamPouleConfigsState = dataManager.subscribeToTeam(team)
    private val selectedRulesetIdFlow = MutableStateFlow<String?>(null)

    val ruleset: Ruleset? = null

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val nevoboTeamName = NevoboTeamName.parse(team.name)

        return consumer.div(classes = "card mb-2 shadow-sm") {
            div (classes = "row g-0 align-items-stretch") {
                div (classes = "col-12 col-sm-1 fw-bold border-end d-flex align-items-center justify-content-center") {
                    +nevoboTeamName.teamIdentifier
                }
                div (classes = "col-12 col-sm") {
                    teamPouleConfigsState.value.forEach { teamPouleConfiguration ->
                        component(TeamPouleConfigConfiguratorComponent(dataManager, rulesetSelectorOffcanvas, teamPouleConfiguration))
                    }
                }
            }
        }
    }

    override suspend fun getState() = teamPouleConfigsState
}

class AllTeamsRuleUpdateTrackerComponent(
    private val dataManager: TeamPouleConfigDataManager
) : HTMLComponent() {
    private val statusMessageFlow = MutableStateFlow<(TagConsumer<HTMLElement>) -> HTMLElement>({ consumer -> consumer.span {}})

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val pendingChanges = dataManager.teamPouleConfigUpdateTracker.value
            .filter { it.key != it.value }
        statusMessageFlow.value = { consumer ->
            consumer.span {
                +"Er zijn "
                strong { +"${pendingChanges.size}" }
                +" wijzigingen"
            }
        }
        return consumer.div {
            if (!pendingChanges.isEmpty()) {
                classes += "d-flex sticky-top bg-primary-subtle p-2 shadow-sm border border-primary align-items-center mb-2".split(" ").toSet()

                button(classes = "btn btn-primary me-2") {
                    type = ButtonType.button
                    onClickFunction = { event ->
                        event.preventDefault()
                        val buttonElement = (event.target as HTMLElement)
                        buttonElement.addClass("disabled")

                        statusMessageFlow.value = { consumer -> consumer.span { spinner(); +" Opslaan..." } }
                        launchAsync(RENDER_SCOPE) {
                            VolleyPlannerApplication.volleyplannerApiPlannerClient
                                .organisation.plannerConfiguration.getSeasonalEndpoint(dataManager.selectedSeasonFlow.value!!)
                                .saveTeamPouleConfigs(pendingChanges.values.toList())
                        }.onComplete {
                            statusMessageFlow.value = { consumer -> consumer.span(classes="text-success fw-bold") { +" Opslaan gelukt!" } }
                            RENDER_SCOPE.async {
                                delay(3000)
                                dataManager.resetAndRefreshData()
                            }
                        }.onError { throwable ->
                            statusMessageFlow.value = { consumer -> consumer.span(classes="text-danger fw-bold") { +" Opslaan mislukt: ${throwable.message}" } }
                            buttonElement.removeClass("disabled")
                        }

                    }
                    +"Opslaan"
                }
                button(classes = "btn btn-secondary me-2") {
                    type = ButtonType.button
                    onClickFunction = { event ->
                        event.preventDefault()
                        RENDER_SCOPE.async { dataManager.resetAndRefreshData() }
                    }

                    +"Reset"
                }

                component(StatusMessageComponent(statusMessageFlow))
            }
        }
    }

    override suspend fun getState() = dataManager.teamPouleConfigUpdateTracker
}

class AllTeamsRuleConfiguratorComponent(
    private val dataManager: TeamPouleConfigDataManager
) : HTMLComponent() {

    override fun onContentReplaced(htmlElement: HTMLElement) {
        super.onContentReplaced(htmlElement)
        val triggerList = document.querySelectorAll("#vp-team-poule-configs-tab button")
        for (i in 0 until triggerList.length) {
            val triggerElement = triggerList[i] as HTMLElement
            val bootstrapTabTrigger = Tab(triggerElement)
            triggerElement.addEventListener("click", { event ->
                event.preventDefault()
                bootstrapTabTrigger.show()
            })
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val rulesetSelectorOffcanvas = RulesetSelectorOffcanvas(dataManager)
        RENDER_SCOPE.async { createStandaloneComponent(rulesetSelectorOffcanvas) }

        val nevoboTeamNames = dataManager.availableTeams.value.map { team -> NevoboTeamName.parse(team.name) }
        val notOverigCompetitionTypes = setOf(NevoboCompetitionType.IndoorVolleyball, NevoboCompetitionType.IndoorRecreationalVolleyball)
        val womenGenderSet = setOf(Gender.Women)
        val menGenderSet = setOf(Gender.Men, Gender.Mixed)
        val categories = listOf<Pair<String, (NevoboTeamName) -> Boolean>>(
            "Dames" to { it.ageGroup == NevoboAgeGroup.Senior && womenGenderSet.contains(it.gender) && it.nevoboCompetitionType == NevoboCompetitionType.IndoorVolleyball },
            "Heren" to { it.ageGroup == NevoboAgeGroup.Senior && menGenderSet.contains(it.gender) && it.nevoboCompetitionType == NevoboCompetitionType.IndoorVolleyball },
            "Meisjes" to { NevoboAgeGroup.YOUTH_GROUPS.contains(it.ageGroup) && womenGenderSet.contains(it.gender) && it.nevoboCompetitionType == NevoboCompetitionType.IndoorVolleyball },
            "Jongens" to { NevoboAgeGroup.YOUTH_GROUPS.contains(it.ageGroup) && menGenderSet.contains(it.gender) && it.nevoboCompetitionType == NevoboCompetitionType.IndoorVolleyball },
            "CMV" to { it.ageGroup == NevoboAgeGroup.CMV && it.nevoboCompetitionType == NevoboCompetitionType.IndoorVolleyball },
            "Recreanten" to { it.nevoboCompetitionType == NevoboCompetitionType.IndoorRecreationalVolleyball },
            "Overig" to { !notOverigCompetitionTypes.contains(it.nevoboCompetitionType) }
        )
        val availableCategories = categories.filter { (_, predicate) -> nevoboTeamNames.any(predicate) }
        if (dataManager.availableTeams.value.isEmpty()) {
            return consumer.div { spinner() }
        }
        return consumer.div {
            component(AllTeamsRuleUpdateTrackerComponent(dataManager))

            ul(classes = "nav nav-tabs") {
                id = "vp-team-poule-configs-tab"
                role = "tablist"

                availableCategories.forEachIndexed { index, (category, test) ->
                    li(classes = "nav-item") {
                        role = "presentation"
                        button(classes = "nav-link") {
                            if (index == 0) classes += "active"
                            id = "vp-tpc-$category-tab"
                            type = ButtonType.button
                            role = "tab"
                            attributes["data-bs-toggle"] = "tab"
                            attributes["data-bs-target"] = "#vp-tpc-$category-pane"
                            attributes["aria-controls"] = "vp-tpc-$category-pane"
                            if (index == 0) {
                                attributes["aria-selected"] = "true"
                            } else {
                                attributes["aria-selected"] = "false"
                            }

                            +category
                        }
                    }
                }
            }

            div(classes = "tab-content") {
                id = "vp-team-poule-configs-tab-content"
                availableCategories.forEachIndexed { index, (category, test) ->
                    div(classes = "tab-pane fade pt-2") {
                        if (index == 0) classes += "show active"
                        id = "vp-tpc-$category-pane"
                        role = "tabpanel"
                        tabIndex = "0"
                        attributes["aria-labelledby"] = "vp-tpc-$category-tab"

                        dataManager.availableTeams.value
                            .filter { team ->
                                val nevoboTeamName = NevoboTeamName.parse(team.name)
                                test(nevoboTeamName)
                            }
                            .forEach { team ->
                                component(SingleTeamRuleConfiguratorComponent(dataManager, rulesetSelectorOffcanvas, team))
                            }
                    }
                }
            }
        }
    }

    override suspend fun getState() = dataManager.availableTeams
}